import { ReactNode } from 'react';
import { AnimatePresence } from 'motion/react';
import { useAgentContext } from '@Providers';

import { useAgents } from '../../CustomHooks';
import useAgentRouteSync from '../../CustomHooks/Agents/useAgentRouteSync';
import useAgentRuntime from '../../CustomHooks/Agents/useAgentRuntime';
import useCanAccessAgent from '../../CustomHooks/Agents/useCanAccessAgent';
import FloatingButton from './Chrome/FloatingButton';
import AgentShell from './Shells/AgentShell';

interface AgentRootProps {
  children: ReactNode;
}

// All agent machinery (data fetch + route sync + shell). Mounted only when the
// user can access agents, so other flavors / unauthorized users never fetch
// /agents or auto-navigate to /agent.
const AgentOverlay = () => {
  const { isOpen, mode, setIsOpen } = useAgentContext();
  const { loadingIds } = useAgentRuntime();
  const { data: agents, isLoading } = useAgents();
  useAgentRouteSync();

  // Mirror pre-#7324 CPFRGlobalWrapper: hide all agent UI when the tenant has no agents configured.
  const hasAgents = !isLoading && !!agents && agents.length > 0;
  if (!hasAgents) return null;

  return (
    <>
      {isOpen && mode === 'sidebar' && (
        <div className="h-full flex-shrink-0" style={{ width: 'var(--bops-agent-sidebar-width, 460px)' }} aria-hidden />
      )}

      <AnimatePresence mode="sync" initial={false}>
        {isOpen && <AgentShell />}
      </AnimatePresence>

      <FloatingButton onClick={() => setIsOpen(true)} isVisible={!isOpen} isPulsing={loadingIds.size > 0} />
    </>
  );
};

const AgentRoot = ({ children }: AgentRootProps) => {
  // Mount the agent subsystem only when the user can access agents. The
  // structural wrapper stays for everyone so the layout is unchanged.
  const canAccessAgent = useCanAccessAgent();

  return (
    <div className="agent-root relative flex h-full w-full">
      <div className="flex-1 min-w-0 overflow-hidden">{children}</div>
      {canAccessAgent && <AgentOverlay />}
    </div>
  );
};

export default AgentRoot;
